import axios from 'axios'
import dayjs from 'dayjs'
import promiseRetry from 'promise-retry'

/**
 * This function capitalizes the first character of a given string.
 * @param  {String} str The string to be formatted
 */
export const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * This function handles the amount of retries for an axios request
 * @param {Function} promiseFn The promise function that will be re-executed
 * @param {'low' | 'medium' | 'high' | undefined} importance Handles the amount of retries where
 * a high priority has more retries than a low priority. undefined will auto-pick a high priority.
 */
export const promiseWithRetry = (promiseFn, importance = 'low') => {
  let retries, factor
  switch (importance) {
    case 'high':
      retries = 10
      factor = 1.36916 // 1 min
      break
    case 'medium':
      retries = 5
      factor = 1.65726 // 30 secs
      break
    case 'low':
      retries = 3
      factor = 1.36549 // 15 secs
      break
    default:
      retries = 10
      factor = 1.36916 // 1 min
  }
  return promiseRetry(
    (retry, _number) =>
      promiseFn().catch(e => {
        retry(e)
      }),
    {
      retries,
      factor,
      minTimeout: 1000,
      randomize: false,
    }
  )
}

/**
 * This function creates an html anchor tag,
 * sets the filename and the href property,
 * auto-clicks it then removes it.
 * @param {String} filename The name of the downloaded file
 * @param {String} href The target href for the downloaded file
 */
export const triggerDownload = (filename, href) => {
  const downloadLink = document.createElement('a')
  downloadLink.href = href
  document.body.appendChild(downloadLink)
  downloadLink.download = filename
  downloadLink.innerHTML = filename
  downloadLink.setAttribute('download', filename)
  downloadLink.click()
  // document.body.removeChild(downloadLink)
  downloadLink.remove()
}

/**
 * This function a string representing the value of the desired cookie
 * or an empty string if no match has been found
 * @param {String} name The name of the cookie
 */
export const getCookieValue = name => {
  const b = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')
  return b ? b.pop() : ''
}

/**
 * This function sets a cookie
 * @param {String} cname The name of the cookie
 * @param {String} cvalue The value of the cookie
 * @param {Number} exdays The days until the cookie expires
 */
export const setCookie = (cname, cvalue, exdays) => {
  const d = new Date()
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000)
  let expires = 'expires=' + d.toUTCString()
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/'
}

/**
 * This function checks if a given object is empty.
 * @param  {Object} obj The object that needs to be checked
 */
export const isEmpty = obj => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      return false
    }
  }
  return true
}

/**
 * This function returns a new FormData object using the properties of the given object.
 * @param {Object} obj The object which properties will be added to the formData
 */
export const createFormData = obj => {
  const formData = new FormData()
  for (const key of Object.keys(obj)) {
    formData.append(key, obj[key])
  }
  return formData
}

/**
 * This function returns the remaining days calculated by a created date and a runtime in days.
 * @param {String} createdDate The created date
 * @param {Number} runtime The max runtime in days
 */
export const getRemainingRuntime = (createdDate, runtime) => {
  const diff = dayjs.utc().diff(dayjs.utc(createdDate), 'days')
  return runtime - diff
}

export const setDesignatorStatus = file => {
  if (file.updateAction === 'Deleted') return 'missing'
  else if (file.updateAction === 'Uploaded') return 'new'
  if (!file.filePath && file.pfStatus === 'urgent') return 'urgent'
  else if (file.pfStatus === 'not-required') return 'not-required'
  else if (file.pfStatus === 'urgent' && file.filePath) return 'new'
  else if (!file.filePath) return 'missing'
  else return file.pfStatus
}

/**
 * This function rounds the given integer/float and formats it into
 * a string in fixed-point notation with max. 2 chars after the point like: "21.42".
 * @param  {Number} num The number that needs to be formatted
 */
export const toAmount = num => parseFloat(Math.round(num * 100) / 100).toFixed(2)

/**
 * This function creates a new html script tag and appends it to the document head.
 * @param {String} src The script source
 * @param {*} id The script element ID
 */
export const scriptLoader = (src, id) => {
  // This function is currently not used
  let elem = document.getElementById(id)
  if (elem) return
  var script = document.createElement('script')
  script.id = id
  // script.onload = callback
  script.src = src
  document.head.appendChild(script)
}

/**
 * This function creates a polyfill for the Object.assign() method.
 */
export const objectAssignPolyfill = () => {
  if (typeof Object.assign !== 'function') {
    Object.assign = function (target) {
      'use strict'
      if (target === null) {
        throw new TypeError('Cannot convert undefined or null to object')
      }
      target = Object(target)
      for (let i = 1; i < arguments.length; i++) {
        const source = arguments[i]
        if (source !== null) {
          for (const key in source) {
            if (Object.prototype.hasOwnProperty.call(source, key)) {
              target[key] = source[key]
            }
          }
        }
      }
      return target
    }
  }
}

/**
 * This function returns an universally unique ID.
 */
export const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })

/**
 * This function generates a random string
 * @param {Number} length The optional length that the generated ID should have. The default length is 6.
 */
export const generateID = (length = 6) => {
  const result = ''
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length))
  }
  return result
}

/**
 * this function decomposes a given URL query into its own params and returns them in an iterable object.
 * @param {String} query The URL query that needs to be decomposed
 */
export const decomposeUrlParams = query => {
  if (!query) {
    return {}
  }
  return (/^[?#]/.test(query) ? query.slice(1) : query).split('&').reduce((params, param) => {
    const [key, value] = param.split('=')
    params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : ''
    return params
  }, {})
}

/**
 * This function calculates the remaining years between 2 years.
 * @param {Number} from The starting year
 * @param {Number | -1} to The end year. -1 uses the current year
 */
export const getYears = (from, to) => {
  if (parseInt(to) === -1) {
    to = new Date().getFullYear()
  }
  return parseInt(to) - parseInt(from)
}

/**
 * This function calculates the remaining months between 2 months.
 * @param {Number} from The starting month
 * @param {Number | -1} to The end month. -1 uses the current month
 */
export const getMonths = (from, to) => {
  if (parseInt(to) === -1) {
    to = new Date().getMonth() + 1
  }
  return parseInt(to) - parseInt(from)
}

/**
 * This function calculates the remaining months between 2 months.
 * @param {Date} d1 The starting date object
 * @param {Date} d2 The end date object
 */
const getMonthsDiff = (d1, d2) => {
  let months
  months = (d2.getFullYear() - d1.getFullYear()) * 12
  months -= d1.getMonth() + 1
  months += d2.getMonth()
  return months <= 0 ? 0 : months
}

/**
 * This function returns an array which contains objects that determine the remaining duration of an item
 * @param {Array<Object>} items The array of items to determine their reamining durations
 */
export const getDurations = items => {
  const durations = []
  items.forEach(item => {
    let toMonth = parseInt(item.toMonth)
    let toYear = parseInt(item.toYear)
    const fromMonth = parseInt(item.fromMonth)
    const fromYear = parseInt(item.fromYear)
    if (toMonth === -1) {
      toMonth = new Date().getMonth() + 1
      toYear = new Date().getFullYear()
    }
    const d1 = new Date(fromYear, fromMonth, 1)
    const d2 = new Date(toYear, toMonth + 1, 1)
    const diff = getMonthsDiff(d1, d2)
    durations.push({
      years: Math.floor(diff / 12),
      months: diff % 12,
    })
  })
  return durations
}

/**
 * This function returns a sort function that handles ascending or descending sorting.
 * @param {String | Number} key
 * @param {'asc' | 'desc'} [order = "asc"] The sort order. The default is set to 'asc'
 */
export const compareValues = (key, order = 'asc') => {
  return function (a, b) {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      return 0
    }
    const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key]
    const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key]
    const comparsion = varA > varB ? 1 : varA < varB ? -1 : 0
    // let comparison = 0
    // if (varA > varB) {
    //   comparison = 1
    // } else if (varA < varB) {
    //   comparison = -1
    // }
    return order === 'desc' ? comparsion * -1 : comparsion
  }
}

/**
 * This function formats a given time
 * @param {Number} time The time that needs to be formatted
 */
export const timerFormat = time => {
  const [timeMin, timeSec] = [Math.floor(time / 60), Math.floor(time % 60)]
  return `0${timeMin}`.slice(-2) + ' : ' + `0${timeSec}`.slice(-2)
}

/**
 * This function formats a date into 'DD/MM/YYYY'
 * @param {String | Number | Date} val The date to be formatted
 */
export const formatDate = (val, format = 'DD/MM/YYYY') => {
  if (!val) return '-'
  return dayjs(val).format(format)
}

/**
 * This function adds additional days to the given date and returns a new Date object.
 * @param {String} date The date which gets additional days
 * @param {Number} days The amount of days to add
 */
export const addDays = (date, days) => {
  const dd = new Date(date)
  return dd.setDate(dd.getDate() + days)
}

/**
 * This function creates an object that contains an interval method and a stop method
 * @param {Function} callback The callback function that will be called in the interval
 * @param {*} ms The interval in milliseconds
 */
export const createInterval = (callback, ms) => {
  return {
    interval: setInterval(callback, ms),
    stop: function () {
      clearInterval(this.interval)
    },
  }
}

/**
 * This function returns a new File() that contains the Video blob.
 * @param {String} url The request URL
 * @param {String} name The filename
 */
export const downloadVideoAsFile = async (url, name) => {
  const video = await axios({
    url,
    method: 'GET',
    responseType: 'blob',
  })
  return new File([video.data], `${name}.mp4`)
}

/**
 * This function returns an array that contains the close-up pages
 * @param {Object} closeup The closeUp object
 */
export const extractCloseupPages = closeup => {
  if (!closeup || !closeup.CloseupPages.length) return []
  const pages = ['page1', 'page2', 'page3', 'page4', 'page5', 'page6', 'page7', 'page8']
  return pages.map(page => closeup.CloseupPages[0][page]).filter(page => page)
}

/**
 * This function closes all the FAQ questions expect the one clicked on
 * @param {Array} questionsMeta An array of QAs
 * @param {number} index The index of the question clicked on
 */
export const closeAllFAQs = (questionsMeta, index) => {
  for (let i = 0; i < questionsMeta.length; i++) {
    if (i === index) {
      continue
    }
    questionsMeta[i].collapsed = true
  }
}
/**
 * This function returns number of days between two dates.
 * @param {dateA} string Valid date string
 * @param {dateB} string Valid date string
 */
export const dateDiffInDays = (dateA, dateB) => {
  const a = new Date(dateA)
  const b = new Date(dateB)
  const _MS_PER_DAY = 1000 * 60 * 60 * 24
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())

  return Math.floor((utc2 - utc1) / _MS_PER_DAY)
}

export const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone
